/**
 * Camunda 7.18 Engine, Metrics, Condition, Migration, and Modification REST API Service
 * https://docs.camunda.org/manual/7.18/reference/rest/engine/
 * https://docs.camunda.org/manual/7.18/reference/rest/metrics/
 * https://docs.camunda.org/manual/7.18/reference/rest/condition/
 * https://docs.camunda.org/manual/7.18/reference/rest/migration/
 * https://docs.camunda.org/manual/7.18/reference/rest/modification/
 */

import { CamundaApiClient, getCamundaApiClient } from '../CamundaApiClient';
import type {
  ProcessEngineInfo,
  Version,
  TelemetryConfiguration,
  Metrics,
  MetricsQuery,
  MetricsInterval,
  EvaluateConditionDto,
  ConditionEvaluationResult,
  MigrationPlan,
  MigrationPlanValidationReport,
  MigrationExecutionDto,
  ModificationDto,
  Batch,
} from '../types';

export class EngineService {
  private client: CamundaApiClient;

  constructor(client?: CamundaApiClient) {
    this.client = client || getCamundaApiClient();
  }

  // ============================================================================
  // Engine
  // ============================================================================

  /**
   * Get process engine names
   */
  async getEngineNames(): Promise<ProcessEngineInfo[]> {
    return this.client.get<ProcessEngineInfo[]>('/engine-rest/engine');
  }

  // ============================================================================
  // Version
  // ============================================================================

  /**
   * Get Camunda version
   */
  async getVersion(): Promise<Version> {
    return this.client.get<Version>('/engine-rest/version');
  }

  // ============================================================================
  // Telemetry
  // ============================================================================

  /**
   * Get telemetry configuration
   */
  async getTelemetryConfiguration(): Promise<TelemetryConfiguration> {
    return this.client.get<TelemetryConfiguration>('/engine-rest/telemetry/configuration');
  }

  /**
   * Set telemetry configuration
   */
  async setTelemetryConfiguration(enableTelemetry: boolean): Promise<void> {
    await this.client.post<void>('/engine-rest/telemetry/configuration', { enableTelemetry });
  }

  /**
   * Get telemetry data
   */
  async getTelemetryData(): Promise<any> {
    return this.client.get<any>('/engine-rest/telemetry/data');
  }

  // ============================================================================
  // Metrics
  // ============================================================================

  /**
   * Get metrics
   */
  async getMetrics(query?: MetricsQuery): Promise<Metrics[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<Metrics[]>(`/engine-rest/metrics${params}`);
  }

  /**
   * Get metrics by interval
   */
  async getMetricsInterval(
    name: string,
    query?: {
      startDate?: string;
      endDate?: string;
      interval?: string;
      aggregateByReporter?: boolean;
    }
  ): Promise<MetricsInterval[]> {
    const params = new URLSearchParams();
    if (query?.startDate) params.append('startDate', query.startDate);
    if (query?.endDate) params.append('endDate', query.endDate);
    if (query?.interval) params.append('interval', query.interval);
    if (query?.aggregateByReporter) params.append('aggregateByReporter', 'true');
    const queryString = params.toString();
    return this.client.get<MetricsInterval[]>(
      `/engine-rest/metrics/${name}${queryString ? `?${queryString}` : ''}`
    );
  }

  /**
   * Get metrics sum
   */
  async getMetricsSum(
    name: string,
    query?: {
      startDate?: string;
      endDate?: string;
    }
  ): Promise<{ result: number }> {
    const params = new URLSearchParams();
    if (query?.startDate) params.append('startDate', query.startDate);
    if (query?.endDate) params.append('endDate', query.endDate);
    const queryString = params.toString();
    return this.client.get<{ result: number }>(
      `/engine-rest/metrics/${name}/sum${queryString ? `?${queryString}` : ''}`
    );
  }

  /**
   * Delete metrics by timestamp
   */
  async deleteMetrics(timestamp: string): Promise<void> {
    await this.client.delete<void>(`/engine-rest/metrics?date=${timestamp}`);
  }

  // ============================================================================
  // Condition
  // ============================================================================

  /**
   * Evaluate conditions
   */
  async evaluateCondition(dto: EvaluateConditionDto): Promise<ConditionEvaluationResult[]> {
    return this.client.post<ConditionEvaluationResult[]>('/engine-rest/condition', dto);
  }

  // ============================================================================
  // Migration
  // ============================================================================

  /**
   * Generate migration plan
   */
  async generateMigrationPlan(
    sourceProcessDefinitionId: string,
    targetProcessDefinitionId: string,
    updateEventTriggers?: boolean
  ): Promise<MigrationPlan> {
    return this.client.post<MigrationPlan>('/engine-rest/migration/generate', {
      sourceProcessDefinitionId,
      targetProcessDefinitionId,
      updateEventTriggers,
    });
  }

  /**
   * Validate migration plan
   */
  async validateMigrationPlan(plan: MigrationPlan): Promise<MigrationPlanValidationReport> {
    return this.client.post<MigrationPlanValidationReport>('/engine-rest/migration/validate', plan);
  }

  /**
   * Execute migration plan
   */
  async executeMigration(dto: MigrationExecutionDto): Promise<void> {
    await this.client.post<void>('/engine-rest/migration/execute', dto);
  }

  /**
   * Execute migration plan async
   */
  async executeMigrationAsync(dto: MigrationExecutionDto): Promise<Batch> {
    return this.client.post<Batch>('/engine-rest/migration/executeAsync', dto);
  }

  // ============================================================================
  // Modification
  // ============================================================================

  /**
   * Execute modification
   */
  async executeModification(dto: ModificationDto): Promise<void> {
    await this.client.post<void>('/engine-rest/modification/execute', dto);
  }

  /**
   * Execute modification async
   */
  async executeModificationAsync(dto: ModificationDto): Promise<Batch> {
    return this.client.post<Batch>('/engine-rest/modification/executeAsync', dto);
  }

  // ============================================================================
  // Schema Log
  // ============================================================================

  /**
   * Get schema log
   */
  async getSchemaLog(query?: {
    version?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    firstResult?: number;
    maxResults?: number;
  }): Promise<any[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<any[]>(`/engine-rest/schema/log${params}`);
  }

  /**
   * Get schema log count
   */
  async getSchemaLogCount(query?: { version?: string }): Promise<{ count: number }> {
    const params = this.buildQueryParams(query);
    return this.client.get<{ count: number }>(`/engine-rest/schema/log/count${params}`);
  }

  // ============================================================================
  // Event Subscription
  // ============================================================================

  /**
   * Get event subscriptions
   */
  async getEventSubscriptions(query?: {
    eventSubscriptionId?: string;
    eventName?: string;
    eventType?: 'message' | 'signal' | 'compensate' | 'conditional';
    executionId?: string;
    processInstanceId?: string;
    activityId?: string;
    tenantIdIn?: string[];
    withoutTenantId?: boolean;
    includeEventSubscriptionsWithoutTenantId?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    firstResult?: number;
    maxResults?: number;
  }): Promise<any[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<any[]>(`/engine-rest/event-subscription${params}`);
  }

  /**
   * Get event subscriptions count
   */
  async getEventSubscriptionsCount(query?: {
    eventSubscriptionId?: string;
    eventName?: string;
    eventType?: 'message' | 'signal' | 'compensate' | 'conditional';
    executionId?: string;
    processInstanceId?: string;
    activityId?: string;
    tenantIdIn?: string[];
    withoutTenantId?: boolean;
    includeEventSubscriptionsWithoutTenantId?: boolean;
  }): Promise<{ count: number }> {
    const params = this.buildQueryParams(query);
    return this.client.get<{ count: number }>(`/engine-rest/event-subscription/count${params}`);
  }

  // ============================================================================
  // Identity
  // ============================================================================

  /**
   * Get groups for user
   */
  async getGroupsForUser(userId: string): Promise<{ groups: any[] }> {
    return this.client.get<{ groups: any[] }>(`/engine-rest/identity/groups?userId=${userId}`);
  }

  /**
   * Verify user password
   */
  async verifyUser(username: string, password: string): Promise<{ authenticatedUser: string; authenticated: boolean }> {
    return this.client.post<{ authenticatedUser: string; authenticated: boolean }>('/engine-rest/identity/verify', {
      username,
      password,
    });
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private buildQueryParams(query?: Record<string, any>): string {
    if (!query) return '';
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else {
          params.append(key, String(value));
        }
      }
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }
}

// Singleton instance
let engineServiceInstance: EngineService | null = null;

export function getEngineService(): EngineService {
  if (!engineServiceInstance) {
    engineServiceInstance = new EngineService();
  }
  return engineServiceInstance;
}

export default EngineService;
